import { useEffect } from 'react'
import { generatePath, useHistory } from 'react-router-dom'

import { colors, GlobalSpinner } from '@taxfix/scouts-ui'

import {
    PATH_COMPANY_RETURN_STATUS,
    PATH_MESSAGES,
    PATH_ROOT,
    PATH_SELF_ASSESSMENT_SUMMARY,
    PATH_TAX_CONSULTATION_STEP_SUMMARY,
} from '@/constants'
import { navigateToApp } from '@/helpers/url-domain'

import { NotificationEventType } from './constants'

const getQueryParams = (queryString: string) => {
    const queryParams = new URLSearchParams(queryString)

    return {
        event: queryParams.get('Event'),
        companyId: queryParams.get('CompanyId'),
        companyReturnId: queryParams.get('CompanyReturnId'),
        selfAssessmentId: queryParams.get('SelfAssessmentId'),
        taxConsultationId: queryParams.get('TaxConsultationId'),
    }
}

/** Redirects the user to the appropriate page based on the mobile push notification type */
const useNotificationRedirect = () => {
    const history = useHistory()

    useEffect(() => {
        const path = (() => {
            const defaultPath = PATH_ROOT

            const { event, companyId, companyReturnId, selfAssessmentId, taxConsultationId } = getQueryParams(
                history.location.search
            )

            switch (event) {
                case NotificationEventType.MessageFromAccountantToCustomer:
                case NotificationEventType.MessageFromSupportToCustomer: {
                    return PATH_MESSAGES
                }

                case NotificationEventType.SelfAssessmentAwaitingCustomerApproval:
                case NotificationEventType.SelfAssessmentDocumentRequested:
                case NotificationEventType.SelfAssessmentFiled: {
                    if (!selfAssessmentId) return defaultPath
                    return `${PATH_SELF_ASSESSMENT_SUMMARY}?activateSelfAssessmentId=${selfAssessmentId}`
                }

                case NotificationEventType.TaxConsultationSummaryAwaitingApproval:
                case NotificationEventType.TaxConsultationToday: {
                    if (!taxConsultationId) return defaultPath
                    return generatePath(PATH_TAX_CONSULTATION_STEP_SUMMARY, { taxConsultationId })
                }

                case NotificationEventType.CompanyReturnStatusReminder:
                case NotificationEventType.CompanyReturnAwaitingCustomerApproval:
                case NotificationEventType.CompanyReturnFiled: {
                    if (!companyId || !companyReturnId) return defaultPath
                    return generatePath(PATH_COMPANY_RETURN_STATUS, { companyId, companyReturnId })
                }

                default: {
                    return defaultPath
                }
            }
        })()

        // For navigation triggered by tapping on the mobile notifications,
        // we need to reload the page to ensure the latest data, e.g. messages, is fetched
        navigateToApp(path)

        // Run this effect only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
}

export const MobileNotifications = () => {
    useNotificationRedirect()

    return <GlobalSpinner color={colors.surface.affirm} />
}
